import React, {
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  Tooltip,
  Button,
} from '@americanexpress/dls-react';
import { useReactlytics } from '@americanexpress/reactlytics';
import clickOutside from '../utils/clickOutside';

export type NavTooltipProps = {
  view: string;
  displayText?: string;
  dismissText?: string;
  isTooltipLoading: boolean;
  isTooltipDismissed: boolean;
  dismissTooltip: () => void;
  children?: React.ReactNode;
};

export type NavTooltipType = React.FC<NavTooltipProps>;

const NavTooltip: NavTooltipType = ({
  view,
  displayText,
  dismissText,
  isTooltipLoading,
  isTooltipDismissed,
  dismissTooltip,
  children,
}) => {
  const { track } = useReactlytics();
  const [isTooltipClosed, setIsTooltipClosed] = useState(false);
  const closeTooltip = useCallback(() => setIsTooltipClosed(true), []);

  const isOpen = view === 'ACCOUNT' && !(isTooltipLoading || isTooltipDismissed || isTooltipClosed);

  useEffect(() => {
    if (isOpen) {
      track({
        event: {
          type: 'impression',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- required
          // @ts-ignore -- requesting SyntheticEvent is incorrect
          element: 'education_tooltip',
        },
      });

      const closeOnClickOutside = clickOutside('#nav-tooltip', closeTooltip);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- required
      // @ts-ignore -- fat arrow function definition is valid
      window.addEventListener('click', closeOnClickOutside);
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- required
      // @ts-ignore -- fat arrow function definition is valid
      return () => window.removeEventListener('click', closeOnClickOutside);
    }
    return () => true;
  // track is not properly memoized
  }, [isOpen, closeTooltip]);

  return (
    <Tooltip
      theme={{
        // apply to not suppress san container width
        width: '100%',
        // wrapper around tooltip and pointer
        '> [role="tooltip"]': {
          left: '50%',
          maxWidth: '250px',
          '@media (min-width: 768px)': {
            left: '43px',
            marginTop: '1px',
          },
        },
        // tooltip content
        '> [role="tooltip"] > div:nth-of-type(2)': {
          lineHeight: '1.5',
          textAlign: 'left',
        },
      }}
      id="nav-tooltip"
      open={isOpen}
      placement="bottom"
      forcePlacement={true}
      triggerElement={children}
      data-testid="nav_tooltip"
    >
      {displayText}
      <Button
        className="dls-white display-block text-underline"
        onClick={dismissTooltip}
        reactlytics={{
          element: 'education_tooltip',
          detail: 'dismiss',
        }}
      >
        {dismissText}
      </Button>
    </Tooltip>
  );
};

export default NavTooltip;
